import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Scissors, AlertCircle, X } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';

export interface PendingClip { jobId: string; title: string; mainJobId: string; nodeId: string; notebookId: string }

/**
 * Follows the export jobs of spin-off clips (parts marked while recording) after the recording
 * dialog has moved on. A finished clip is handed back as a file so the app can open it as its own
 * upload flow; until then a small pill shows that clips are still being rendered.
 */
export default function ClipJobsWatcher({ clips, uid, vn, onReady, onGone }: {
    clips: PendingClip[];
    uid?: string;
    vn: boolean;
    onReady: (clip: PendingClip, file: File) => void;
    /** The clip is finished with here (opened, or failed and dismissed). */
    onGone: (jobId: string) => void;
}) {
    const [progress, setProgress] = useState<Record<string, number | null>>({});
    const [failed, setFailed] = useState<Record<string, string>>({});
    const subs = useRef(new Map<string, () => void>());
    const handed = useRef(new Set<string>());

    useEffect(() => {
        if (!uid) return;
        for (const clip of clips) {
            if (subs.current.has(clip.jobId)) continue;
            const unsub = onSnapshot(doc(db, `users/${uid}/videoExportJobs/${clip.jobId}`), async snap => {
                const data = snap.data();
                if (!data) return;
                if (data.status === 'pending' || data.status === 'processing') {
                    setProgress(p => ({ ...p, [clip.jobId]: typeof data.progress === 'number' ? data.progress : null }));
                } else if (data.status === 'done' && !handed.current.has(clip.jobId)) {
                    handed.current.add(clip.jobId);
                    try {
                        const url = await getDownloadURL(storageRef(storage, data.storagePathFinal));
                        const blob = await (await fetch(url)).blob();
                        onReady(clip, new File([blob], `studyboard-clip-${Date.now()}.mp4`, { type: 'video/mp4' }));
                        onGone(clip.jobId);
                    } catch (e: any) {
                        handed.current.delete(clip.jobId);
                        setFailed(f => ({ ...f, [clip.jobId]: e?.message || 'download failed' }));
                    }
                } else if (data.status === 'error') {
                    setFailed(f => ({ ...f, [clip.jobId]: data.error || 'export failed' }));
                }
            }, () => { /* listener errors are not fatal: the job stays recoverable from YouTube Manager */ });
            subs.current.set(clip.jobId, unsub);
        }
        // Stop following clips that are no longer pending.
        for (const [jobId, unsub] of subs.current) {
            if (!clips.some(c => c.jobId === jobId)) { unsub(); subs.current.delete(jobId); }
        }
    }, [clips, uid]);
    useEffect(() => () => { subs.current.forEach(u => u()); subs.current.clear(); }, []);

    if (clips.length === 0) return null;
    const bad = clips.filter(c => failed[c.jobId]);
    const working = clips.filter(c => !failed[c.jobId]);
    const pcts = working.map(c => progress[c.jobId]).filter((n): n is number => typeof n === 'number');
    const avg = pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null;

    return (
        <div className="fixed left-5 bottom-5 z-[150] flex flex-col gap-2 max-w-xs">
            {working.length > 0 && (
                <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-2xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900 shadow-xl" title={working.map(c => c.title).join('\n')}>
                    <Loader2 size={16} className="animate-spin text-emerald-500 flex-shrink-0" />
                    <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-800 dark:text-gray-100 flex items-center gap-1"><Scissors size={11} /> {vn ? `${working.length} video con đang xử lý` : `${working.length} clip${working.length > 1 ? 's' : ''} rendering`}{avg != null ? ` · ${avg}%` : ''}</p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{vn ? 'Xong sẽ hiện thành ô "Sẵn sàng upload" ở góc phải' : 'Each appears as "Ready to upload" in the right corner'}</p>
                    </div>
                </div>
            )}
            {bad.map(c => (
                <div key={c.jobId} className="flex items-center gap-2 px-3.5 py-2.5 rounded-2xl bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900 shadow-xl">
                    <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{vn ? 'Video con lỗi' : 'Clip failed'}: {c.title}</p>
                        <p className="text-[11px] text-red-500 truncate" title={failed[c.jobId]}>{failed[c.jobId]}</p>
                    </div>
                    <button onClick={() => onGone(c.jobId)} className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={13} /></button>
                </div>
            ))}
        </div>
    );
}
